// src/features/projects/ProjectEditPanel.jsx
//
// Shared create/edit modal for project scalar fields.
//
// Projects are primarily an information surface, so the default form asks for
// only the identity fields needed to create a project. Edit shows all scalar
// fields immediately; create keeps them behind More details.

import React, { useCallback, useEffect, useRef, useState } from "react";
import { T, PROJECT_TYPE, PROJECT_PHASE, PROJECT_STATUS } from "../../app/tokens.js";
import { Modal } from "../../components/Modal.jsx";
import { DateInput } from "../../components/DateInput.jsx";
import { createProject, updateProject, getProject, archiveProject } from "../../data/projectsClient.js";
import { ArchiveConfirmDialog } from "../../components/ArchiveConfirmDialog.jsx";
import { getLocations } from "../../data/locationsClient.js";
import { parseNumericField } from "../../utils/parseNumericField.js";
import { log } from "../../utils/logger.js";
import { showToast } from "../../components/toastService.jsx";
import { isSlackInvite, validSlackWrite, SLACK_URL_ERROR, SLACK_INVITE_HINT } from "../../lib/projectSlackUrl.js";

const CURRENCIES = ["USD", "GBP", "EUR", "AUD", "CAD", "SGD", "AED", "CHF", "JPY", "BRL", "CNY", "INR", "ISK"];

const EMPTY_FORM = {
  locationId: "",
  name: "",
  projectType: "",
  phase: "",
  statusOverall: "",
  approvedBudget: "",
  financialsTracked: true,
  currency: "",
  startDate: "",
  fdobDate: "",
  internalOwner: "",
  jllOwner: "",
  slackChannel: "",
  oneLineSummary: "",
  notes: "",
};

export function projectDateInput(value) {
  if (value instanceof Date) return Number.isFinite(value.getTime()) ? value.toISOString().slice(0, 10) : "";
  return typeof value === "string" && /^\d{4}-\d{2}-\d{2}(?:T|$)/.test(value) ? value.slice(0, 10) : "";
}

function formFromProject(project) {
  return {
    ...EMPTY_FORM,
    locationId: project?.locationId ?? "",
    name: project?.name ?? "",
    projectType: project?.projectType ?? "",
    phase: project?.phase ?? "",
    statusOverall: project?.statusOverall ?? "",
    approvedBudget: project?.approvedBudget != null ? String(project.approvedBudget) : "",
    financialsTracked: project?.financialsTracked !== false,
    currency: project?.currency ?? "",
    startDate: projectDateInput(project?.startDate),
    fdobDate: projectDateInput(project?.fdobDate),
    internalOwner: project?.internalOwner ?? "",
    jllOwner: project?.jllOwner ?? "",
    slackChannel: project?.slackChannel ?? "",
    oneLineSummary: project?.oneLineSummary ?? "",
    notes: project?.notes ?? "",
  };
}

function addIfPresent(payload, key, value) {
  if (value !== "" && value !== undefined && value !== null) payload[key] = value;
}

function createPayload(form) {
  const payload = {
    locationId: form.locationId.trim(),
    name: form.name.trim(),
    projectType: form.projectType,
  };
  [
    "phase",
    "statusOverall",
    "currency",
    "startDate",
    "fdobDate",
    "internalOwner",
    "jllOwner",
    "slackChannel",
    "oneLineSummary",
    "notes",
  ].forEach((key) => addIfPresent(payload, key, form[key]));

  const approvedBudget = parseNumericField(form.approvedBudget);
  if (approvedBudget !== null) payload.approvedBudget = approvedBudget;
  return payload;
}

function normalizedEdit(form) {
  return {
    locationId: form.locationId.trim(),
    name: form.name.trim(),
    projectType: form.projectType,
    // phase is nullable in the database, so clearing it must be explicit.
    phase: form.phase || null,
    // status_overall is NOT NULL; blank edit state resolves to the neutral value.
    statusOverall: form.statusOverall || "on_track",
    approvedBudget: parseNumericField(form.approvedBudget),
    financialsTracked: form.financialsTracked,
    currency: form.currency || null,
    startDate: form.startDate || null,
    fdobDate: form.fdobDate || null,
    internalOwner: form.internalOwner || null,
    jllOwner: form.jllOwner || null,
    slackChannel: form.slackChannel.trim() || null,
    oneLineSummary: form.oneLineSummary || null,
    notes: form.notes || null,
  };
}

function editPayload(form, baseline, canSeeBudget) {
  const current = normalizedEdit(form);
  const loaded = normalizedEdit(baseline);
  return Object.fromEntries(Object.entries(current).filter(([key, value]) =>
    (canSeeBudget || !["approvedBudget", "currency", "financialsTracked"].includes(key)) && value !== loaded[key],
  ));
}

const INPUT_STYLE = {
  width: "100%",
  boxSizing: "border-box",
  fontFamily: "inherit",
  border: `1px solid ${T.border}`,
  borderRadius: 6,
  padding: "6px 8px",
  fontSize: T.fsSm5,
  color: T.dark,
  background: T.white,
};
const LABEL_STYLE = {
  display: "block",
  color: T.mid,
  fontSize: T.fsXs,
  fontWeight: T.fwSemibold,
  marginBottom: 4,
};
const FIELD_STYLE = { minWidth: 0 };
const STATUS_OPTIONS = [
  ["", "Not set"],
  ...Object.entries(PROJECT_STATUS).map(([key, value]) => [key, value.label]),
];

function Field({ label, children, required = false }) {
  return (
    <label style={FIELD_STYLE}>
      <span style={LABEL_STYLE}>{label}{required ? " *" : ""}</span>
      {children}
    </label>
  );
}

function TextInput({ value, onChange, ...props }) {
  return <input {...props} value={value} onChange={(e) => onChange(e.target.value)} style={INPUT_STYLE} />;
}

function SelectInput({ value, onChange, children, palette, ...props }) {
  const selected = palette?.[value];
  return (
    <select
      {...props}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{
        ...INPUT_STYLE,
        cursor: "pointer",
        ...(selected && { color: selected.color, background: selected.bg, fontWeight: T.fwSemibold }),
      }}
    >
      {children}
    </select>
  );
}

function normalizeOffice(location) {
  if (!location?.id) return null;
  return {
    id: String(location.id),
    region: location.region || "—",
    market: location.market || location.marketName || "—",
    name: location.locationAlias || location.name || location.locationName || String(location.id),
  };
}

function officeLabel(office) {
  return `${office.region} · ${office.market} · ${office.name} (${office.id})`;
}

function OfficePicker({ testPrefix, selectedId, onSelect, onInventoryChange, disabled }) {
  const [offices, setOffices] = useState([]);
  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [reloadKey, setReloadKey] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setLoadError(null);
    getLocations()
      .then((rows) => {
        if (cancelled) return;
        const normalized = (Array.isArray(rows) ? rows : [])
          .filter((location) => location?.deletedAt == null && location?.deleted_at == null)
          .map(normalizeOffice)
          .filter(Boolean);
        setOffices(normalized);
        onInventoryChange(normalized);
      })
      .catch((err) => {
        if (!cancelled) {
          setOffices([]);
          setLoadError(err?.message || "Could not load offices.");
          onInventoryChange([]);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [onInventoryChange, reloadKey]);

  const selectedOffice = offices.find((office) => office.id === selectedId) || null;

  useEffect(() => {
    if (!loading && !loadError && selectedId && !selectedOffice) {
      onSelect("");
    }
  }, [loading, loadError, onSelect, selectedId, selectedOffice]);

  const foldedQuery = (searching ? query : "").trim().toLocaleLowerCase();
  const matches = offices.filter((office) => {
    if (!foldedQuery) return true;
    return officeLabel(office).toLocaleLowerCase().includes(foldedQuery)
      || office.id.toLocaleLowerCase().includes(foldedQuery)
      || office.name.toLocaleLowerCase().includes(foldedQuery);
  });

  function choose(office) {
    onSelect(office.id);
    setQuery("");
    setSearching(false);
    setActiveIndex(0);
    setIsOpen(false);
  }

  function handleKeyDown(event) {
    if (event.key === "ArrowDown") {
      event.preventDefault();
      setIsOpen(true);
      setSearching(true);
      setActiveIndex((index) => isOpen ? Math.min(index + 1, Math.max(matches.length - 1, 0)) : 0);
    } else if (event.key === "ArrowUp") {
      event.preventDefault();
      setIsOpen(true);
      setSearching(true);
      setActiveIndex((index) => Math.max(index - 1, 0));
    } else if (event.key === "Enter" && isOpen && matches[activeIndex]) {
      event.preventDefault();
      choose(matches[activeIndex]);
    } else if (event.key === "Escape") {
      event.stopPropagation();
      setIsOpen(false);
      if (selectedId) {
        setSearching(false);
        setQuery("");
      }
    }
  }

  const listId = `${testPrefix}-office-options`;
  return (
    <div style={{ position: "relative" }}>
      <input
        type="text"
        role="combobox"
        aria-label="Search office locations"
        aria-expanded={isOpen}
        aria-controls={listId}
        aria-autocomplete="list"
        aria-activedescendant={isOpen && matches[activeIndex] ? `${testPrefix}-office-option-${matches[activeIndex].id}` : undefined}
        data-testid={testPrefix === "add-project" ? "add-project-location-id" : "edit-field-locationId"}
        value={searching ? query : selectedOffice ? officeLabel(selectedOffice) : ""}
        onFocus={() => {
          setSearching(true);
          setQuery("");
          setActiveIndex(0);
          setIsOpen(true);
        }}
        onBlur={() => {
          setIsOpen(false);
          if (selectedId) {
            setSearching(false);
            setQuery("");
          }
        }}
        onChange={(event) => {
          setQuery(event.target.value);
          setSearching(true);
          onSelect("");
          setActiveIndex(0);
          setIsOpen(true);
        }}
        onKeyDown={handleKeyDown}
        placeholder="Search by office name or ID"
        disabled={disabled || loading || !!loadError || offices.length === 0}
        style={INPUT_STYLE}
      />
      {loading && <div data-testid={`${testPrefix}-office-loading`} style={{ color: T.mid, fontSize: T.fsXs, marginTop: 4 }}>Loading offices…</div>}
      {loadError && (
        <div role="alert" data-testid={`${testPrefix}-office-load-error`} style={{ color: T.red, fontSize: T.fsXs, marginTop: 4 }}>
          {loadError}{" "}
          <button type="button" onClick={() => setReloadKey((key) => key + 1)} disabled={disabled} style={{ border: 0, padding: 0, color: T.blue, background: "transparent", textDecoration: "underline", cursor: "pointer" }}>Retry</button>
        </div>
      )}
      {!loading && !loadError && offices.length === 0 && (
        <div data-testid={`${testPrefix}-office-empty`} style={{ color: T.mid, fontSize: T.fsXs, marginTop: 4 }}>No active offices available.</div>
      )}
      {!loading && !loadError && isOpen && offices.length > 0 && (
        <div
          id={listId}
          role="listbox"
          data-testid={`${testPrefix}-office-list`}
          style={{ position: "absolute", zIndex: 10, left: 0, right: 0, top: "100%", maxHeight: 220, overflowY: "auto", marginTop: 3, padding: 3, border: `1px solid ${T.border}`, borderRadius: 6, background: T.white, boxShadow: "0 4px 12px rgba(0,0,0,.12)" }}
        >
          {matches.length === 0 ? (
            <div data-testid={`${testPrefix}-office-no-match`} style={{ padding: 8, color: T.mid, fontSize: T.fsSm }}>No offices match that search.</div>
          ) : matches.map((office, index) => (
            <div
              key={office.id}
              id={`${testPrefix}-office-option-${office.id}`}
              role="option"
              aria-selected={office.id === selectedId}
              data-testid={`${testPrefix}-office-option-${office.id}`}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => choose(office)}
              style={{ padding: "7px 8px", borderRadius: 4, background: index === activeIndex ? T.blueSoft : T.white, color: T.dark, fontSize: T.fsSm, cursor: "pointer" }}
            >
              {officeLabel(office)}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function SectionLabel({ children }) {
  return (
    <div style={{
      color: T.mid, fontSize: T.fsXs, fontWeight: T.fwBold,
      letterSpacing: ".05em", textTransform: "uppercase",
      margin: "16px 0 8px", paddingBottom: 5, borderBottom: `1px solid ${T.border}`,
    }}>
      {children}
    </div>
  );
}

/**
 * Shared project create/edit modal.
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {"create"|"edit"} props.mode
 * @param {object|null} props.project
 * @param {Function} props.onClose
 * @param {Function} [props.onCreated]
 * @param {Function} [props.onSaved]
 */
export function ProjectEditModal({
  isOpen,
  mode = "edit",
  project = null,
  onClose,
  onCreated,
  onSaved,
  onBusyChange,
  guardUnsaved = false,
  canSeeBudget = true,
  canArchive = false,
  onArchived,
}) {
  const isCreate = mode === "create";
  const [form, setForm] = useState(() => formFromProject(project));
  // Advance only acknowledged fields after each save. A failed second step must
  // leave its draft dirty without resending metadata that already committed.
  const [baseline, setBaseline] = useState(() => formFromProject(project));
  const savedRecord = useRef(project);
  const needsRefresh = useRef(false);
  const metadataSaved = useRef(false);
  const [moreDetails, setMoreDetails] = useState(mode !== "create");
  const [saving, setSaving] = useState(false);
  const [archiveOpen, setArchiveOpen] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const mutation = useRef(false);
  const busy = saving || archiving;
  const [error, setError] = useState(null);
  const [slackError, setSlackError] = useState(null);
  const [selectedLocationId, setSelectedLocationId] = useState(() => formFromProject(project).locationId);
  const [officeInventory, setOfficeInventory] = useState([]);
  const [closeAsk, setCloseAsk] = useState(false);
  const keepEditingFocus = useRef(null);
  const editorFocused = useRef(false);
  // In-flight saves are reported to owners (record drawer guard) and block
  // Escape/backdrop/close so a pending mutation is never silently abandoned.
  const busyCb = useRef(onBusyChange);
  busyCb.current = onBusyChange;
  useEffect(() => { busyCb.current?.(busy); }, [busy]);
  useEffect(() => () => busyCb.current?.(false), []);

  const handleOfficeSelect = useCallback((locationId) => {
    setSelectedLocationId(locationId);
    setForm((current) => ({ ...current, locationId }));
  }, []);

  useEffect(() => {
    if (!isOpen) return;
    const nextForm = formFromProject(project);
    setForm(nextForm);
    setBaseline(nextForm);
    savedRecord.current = project;
    needsRefresh.current = false;
    metadataSaved.current = false;
    setSelectedLocationId(nextForm.locationId);
    setOfficeInventory([]);
    setMoreDetails(mode !== "create");
    setError(null);
    setSlackError(null);
    setSaving(false);
    setArchiveOpen(false);
    setArchiving(false);
    setCloseAsk(false);
    editorFocused.current = false;
  }, [isOpen, project, mode]);

  useEffect(() => {
    if (!isOpen || !guardUnsaved || archiveOpen) return undefined;
    const dialog = document.querySelector('[data-testid="project-edit-modal"]');
    const controls = () => [...(dialog?.querySelectorAll('button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), a[href]') || [])]
      .filter(el => !el.closest("[hidden]") && el.getClientRects().length);
    if (!editorFocused.current) {
      controls()[0]?.focus();
      editorFocused.current = true;
    }
    const trap = e => {
      if (e.key !== "Tab") return;
      const items = controls();
      if (!items.length) return;
      const first = items[0], last = items.at(-1);
      if (!items.includes(document.activeElement) || (e.shiftKey && document.activeElement === first)) { e.preventDefault(); (e.shiftKey ? last : first).focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    document.addEventListener("keydown", trap, true);
    return () => document.removeEventListener("keydown", trap, true);
  }, [isOpen, guardUnsaved, archiveOpen]);

  if (!isOpen) return null;

  const requestClose = () => {
    if (mutation.current || archiveOpen) return;
    const dirty = isCreate
      ? JSON.stringify(form) !== JSON.stringify(baseline)
      : Object.keys(editPayload(form, baseline, canSeeBudget)).length > 0;
    if (guardUnsaved && dirty) {
      if (!closeAsk) keepEditingFocus.current = document.activeElement;
      setCloseAsk(true); return;
    }
    onClose();
  };
  const set = (key) => (value) => setForm((current) => ({ ...current, [key]: value }));
  const trackingBlocked = !isCreate && !form.financialsTracked && (
    parseNumericField(form.approvedBudget) !== null ||
    [savedRecord.current?.approvedBudget, savedRecord.current?.spentToDate, savedRecord.current?.forecastAtCompletion].some(value => value != null)
  );

  async function handleSubmit(event) {
    event.preventDefault();
    if (mutation.current || archiveOpen) return;
    setError(null);
    if (trackingBlocked) return setError("Budget, Spend and Forecast must all be empty before turning off financial tracking.");
    const slackChanged = isCreate || normalizedEdit(form).slackChannel !== normalizedEdit(baseline).slackChannel;
    if (slackChanged && !validSlackWrite(form.slackChannel)) {
      setSlackError(SLACK_URL_ERROR);
      document.querySelector('[data-testid="edit-field-slackChannel"]')?.focus();
      return;
    }
    setSlackError(null);

    if (!officeInventory.some((office) => office.id === selectedLocationId)) {
      return setError("Select an office location from the list.");
    }
    if (!form.name.trim()) return setError("Project name is required.");
    if (!form.projectType) return setError("Project type is required.");

    mutation.current = true;
    setSaving(true);
    let savingBudget = false;
    let reloading = false;
    try {
      const payload = isCreate ? createPayload(form) : editPayload(form, baseline, canSeeBudget);
      if (!canSeeBudget) {
        delete payload.approvedBudget;
        delete payload.currency;
      }
      let result;
      if (isCreate) {
        result = await createProject(payload);
      } else {
        const { approvedBudget, financialsTracked, ...metadata } = payload;
        const acknowledge = (patch, record) => {
          savedRecord.current = record;
          needsRefresh.current = true;
          setBaseline(current => ({
            ...current,
            ...Object.fromEntries(Object.keys(patch).map(key => [key, form[key]])),
          }));
        };
        if (Object.keys(metadata).length) {
          const record = await updateProject(project.id, metadata);
          acknowledge(metadata, record);
          metadataSaved.current = true;
        }
        if ("financialsTracked" in payload) {
          const tracking = { financialsTracked };
          const record = await updateProject(project.id, tracking);
          acknowledge(tracking, record);
        }
        if ("approvedBudget" in payload) {
          savingBudget = true;
          // This existing PATCH branch delegates to saveWeeklyFinancials with
          // server-selected current week and all financial authorization intact.
          const financials = { approvedBudget };
          const record = await updateProject(project.id, financials);
          acknowledge(financials, record);
          savingBudget = false;
        }
        // Financial PATCH returns a raw row, not the enriched record. Re-read
        // after writes so Connections and every modal caller receive current data.
        reloading = needsRefresh.current;
        result = reloading ? await getProject(project.id) : savedRecord.current;
        savedRecord.current = result;
        needsRefresh.current = false;
      }
      showToast(isCreate ? "Project created" : "Project saved", { type: "success" });
      if (isCreate) onCreated?.(result);
      else onSaved?.(result);
      onClose();
    } catch (err) {
      log.warn(`ProjectEditModal: ${isCreate ? "create" : "save"} failed`, { err: String(err) });
      const detail = err?.message || "Please try again.";
      setError(savingBudget
        ? `Approved budget was not saved. ${metadataSaved.current ? "Project metadata already saved; retry will save only remaining changes." : "No project metadata was changed."} ${detail}`
        : reloading
          ? `Changes saved, but the project could not be reloaded. Retry to reload without saving them again. ${detail}`
          : detail);
    } finally {
      mutation.current = false;
      setSaving(false);
    }
  }

  async function handleArchive(reason) {
    if (mutation.current || isCreate || !canArchive || !project?.id) return;
    mutation.current = true;
    setArchiving(true);
    setError(null);
    try {
      await archiveProject(project.id, reason);
    } catch (err) {
      setError(err.message);
      setArchiveOpen(false);
      return;
    } finally {
      mutation.current = false;
      setArchiving(false);
    }
    showToast("Project archived - restore from Recently Deleted", { type: "success" });
    onArchived?.(project.id);
    onClose();
  }

  const testPrefix = isCreate ? "add-project" : "edit-project";
  const title = isCreate ? "+ Add project" : "Edit project";
  const modalTestId = isCreate ? "add-project-modal" : "project-edit-modal";

  return (
    <>
    <Modal
      isOpen
      onClose={requestClose}
      maxWidth={620}
      title={title}
      subTitle={isCreate ? "Start with the essentials; add project detail when you have it." : project?.name}
      banner
      testId={modalTestId}
      overlayTestId={`${testPrefix}-modal-overlay`}
      closeTestId={`${testPrefix}-modal-close`}
      ariaLabelledBy={`${testPrefix}-modal-title`}
    >
      <form onSubmit={handleSubmit} style={{ display: "flex", flex: 1, flexDirection: "column", minHeight: 0 }}>
        <div data-testid={`${testPrefix}-modal-body`} style={{ flex: 1, overflowY: "auto", padding: "12px 20px 16px" }}>
          {closeAsk && <div role="alert" data-testid="project-edit-unsaved" style={{ padding: 10, marginBottom: 12, background: T.utilAmber, fontSize: T.fsSm }}>
            <p style={{ margin: "0 0 8px" }}>Discard the unsaved record changes?</p>
            <button type="button" onClick={() => {
              setCloseAsk(false);
              const dialog = document.querySelector('[data-testid="project-edit-modal"]');
              const target = keepEditingFocus.current;
              if (target?.isConnected && dialog?.contains(target)) target.focus();
              else dialog?.querySelector("input:not([disabled]), button:not([disabled])")?.focus();
            }}>Keep editing</button>
            <button type="button" disabled={busy || archiveOpen} onClick={() => { if (!mutation.current && !archiveOpen) onClose(); }} style={{ marginLeft: 8 }}>Discard changes</button>
          </div>}
          {error && (
            <div
              role="alert"
              data-testid={isCreate ? "add-project-error" : "project-edit-error"}
              style={{ marginBottom: 12, padding: "8px 10px", borderRadius: 6, background: T.redSoft, border: `1px solid ${T.redLight}`, color: T.red, fontSize: T.fsSm }}
            >
              {error}
            </div>
          )}

          <div style={{ display: "grid", gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr)", gap: 10 }}>
            <Field label="Office" required>
              <OfficePicker
                testPrefix={testPrefix}
                selectedId={selectedLocationId}
                onSelect={handleOfficeSelect}
                onInventoryChange={setOfficeInventory}
                disabled={saving}
              />
            </Field>
            <Field label="Project name" required>
              <TextInput
                data-testid={isCreate ? "add-project-name" : "edit-field-name"}
                value={form.name}
                onChange={set("name")}
                placeholder="e.g. London Fit-Out Phase 3"
                disabled={saving}
              />
            </Field>
          </div>

          <div style={{ marginTop: 10 }}>
            <Field label="Project type" required>
              <SelectInput
                data-testid={isCreate ? "add-project-type" : "edit-field-projectType"}
                value={form.projectType}
                onChange={set("projectType")}
                palette={PROJECT_TYPE}
                disabled={saving}
              >
                <option value="">Select type…</option>
                {Object.entries(PROJECT_TYPE).map(([key, value]) => <option key={key} value={key} style={{ color: value.color, background: value.bg }}>{value.label}</option>)}
              </SelectInput>
            </Field>
          </div>

          <button
            type="button"
            data-testid="project-more-details-toggle"
            aria-expanded={moreDetails}
            onClick={() => setMoreDetails((expanded) => !expanded)}
            style={{
              display: "flex", alignItems: "center", justifyContent: "space-between",
              width: "100%", marginTop: 14, padding: "9px 0",
              border: 0, borderTop: `1px solid ${T.border}`, borderBottom: moreDetails ? `1px solid ${T.border}` : 0,
              background: "transparent", color: T.blue, fontSize: T.fsSm, fontWeight: T.fwSemibold,
              cursor: "pointer", textAlign: "left",
            }}
          >
            <span>More details</span>
            <span aria-hidden="true">{moreDetails ? "⌃" : "⌄"}</span>
          </button>

          {moreDetails && (
            <div data-testid="project-more-details" style={{ paddingTop: 2 }}>
              <SectionLabel>Schedule and status</SectionLabel>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                <Field label="Phase">
                  <SelectInput data-testid={isCreate ? "add-project-phase" : "edit-field-phase"} value={form.phase} onChange={set("phase")} disabled={saving}>
                    <option value="">Not set</option>
                    {Object.entries(PROJECT_PHASE).map(([key, value]) => <option key={key} value={key}>{value.label}</option>)}
                  </SelectInput>
                </Field>
                <Field label="Overall health">
                  <SelectInput data-testid={isCreate ? "add-project-status" : "edit-field-statusOverall"} value={form.statusOverall} onChange={set("statusOverall")} palette={PROJECT_STATUS} disabled={saving}>
                    {STATUS_OPTIONS.map(([key, label]) => <option key={key} value={key} style={PROJECT_STATUS[key] ? { color: PROJECT_STATUS[key].color, background: PROJECT_STATUS[key].bg } : undefined}>{label}</option>)}
                  </SelectInput>
                </Field>
              </div>
              {canSeeBudget && <>
              <SectionLabel>Budget</SectionLabel>
              {!isCreate && <div style={{ marginBottom: 10 }}>
                <label style={{ color: T.dark, fontSize: T.fsSm }}>
                  <input type="checkbox" checked={form.financialsTracked} disabled={saving}
                    onChange={event => set("financialsTracked")(event.target.checked)} />
                  {" "}Track financials for this project
                </label>
                {trackingBlocked && <div role="alert" style={{ color: T.red, fontSize: T.fsXs }}>
                  Budget, Spend and Forecast must all be empty before turning off financial tracking.
                </div>}
              </div>}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 120px", gap: 10 }}>
                <Field label="Approved budget">
                  <TextInput data-testid={isCreate ? "add-project-budget" : "edit-field-approvedBudget"} type="number" min="0" value={form.approvedBudget} onChange={set("approvedBudget")} placeholder="0" disabled={saving} />
                </Field>
                <Field label="Currency">
                  <SelectInput data-testid={isCreate ? "add-project-currency" : "edit-field-currency"} value={form.currency} onChange={set("currency")} disabled={saving}>
                    <option value="">Not set</option>
                    {CURRENCIES.map((currency) => <option key={currency} value={currency}>{currency}</option>)}
                  </SelectInput>
                </Field>
              </div>

              </>}
              <SectionLabel>Dates and owners</SectionLabel>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                {[
                  ["startDate", "Start date"],
                  ["fdobDate", "FDOB"],
                ].map(([key, label]) => (
                  <Field key={key} label={label}>
                    <DateInput data-testid={`edit-field-${key}`} value={form[key]} onChange={(event) => set(key)(event.target.value)} disabled={saving} />
                  </Field>
                ))}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginTop: 10 }}>
                <Field label="Internal owner">
                  <TextInput data-testid="edit-field-internalOwner" value={form.internalOwner} onChange={set("internalOwner")} disabled={saving} />
                </Field>
                <Field label="JLL owner">
                  <TextInput data-testid="edit-field-jllOwner" value={form.jllOwner} onChange={set("jllOwner")} disabled={saving} />
                </Field>
              </div>

              <SectionLabel>Notes and connections</SectionLabel>
              <Field label="Slack channel">
                <TextInput data-testid="edit-field-slackChannel" value={form.slackChannel}
                  onChange={value => { set("slackChannel")(value); setSlackError(null); }}
                  placeholder="https://workspace.slack.com/archives/C…"
                  aria-invalid={Boolean(slackError)}
                  aria-describedby={slackError ? "project-slack-error" : isSlackInvite(form.slackChannel) ? "project-slack-hint" : undefined}
                  disabled={saving} />
              </Field>
              {slackError && <div id="project-slack-error" role="alert" style={{ color: T.red, fontSize: T.fsXs, marginTop: 4 }}>{slackError}</div>}
              {isSlackInvite(form.slackChannel) && <div id="project-slack-hint" style={{ color: T.mid, fontSize: T.fsXs, marginTop: 4 }}>{SLACK_INVITE_HINT}</div>}
              <div style={{ marginTop: 10 }}>
                <Field label="One-line summary">
                  <TextInput data-testid="edit-field-oneLineSummary" value={form.oneLineSummary} onChange={set("oneLineSummary")} disabled={saving} />
                </Field>
              </div>
              <div style={{ marginTop: 10 }}>
                <Field label="Internal notes">
                  <textarea data-testid="edit-field-notes" value={form.notes} onChange={(event) => set("notes")(event.target.value)} disabled={saving} rows={3} style={{ ...INPUT_STYLE, resize: "vertical" }} />
                </Field>
              </div>
            </div>
          )}
        </div>

        <div style={{ display: "flex", justifyContent: "space-between", gap: 8, padding: "10px 20px 14px", borderTop: `1px solid ${T.border}`, flexShrink: 0 }}>
          <div>{!isCreate && canArchive && <button type="button" data-testid="project-edit-archive-btn" disabled={busy} onClick={() => { if (!mutation.current) { setCloseAsk(false); setArchiveOpen(true); } }} style={{ border: `1px solid ${T.redLight}`, borderRadius: 6, background: T.white, color: T.red, padding: "7px 14px", fontSize: T.fsSm, cursor: "pointer" }}>Archive</button>}</div>
          <div style={{ display: "flex", gap: 8 }}>
          <button type="button" data-testid={isCreate ? "add-project-cancel" : "project-edit-cancel-btn"} onClick={requestClose} disabled={busy || archiveOpen} style={{ border: `1px solid ${T.border}`, borderRadius: 6, background: T.white, color: T.mid, padding: "7px 14px", fontSize: T.fsSm, cursor: "pointer" }}>
            Cancel
          </button>
          <button type="submit" data-testid={isCreate ? "add-project-submit" : "project-edit-save-btn"} disabled={busy || archiveOpen} style={{ border: 0, borderRadius: 6, background: T.blue, color: T.white, padding: "7px 14px", fontSize: T.fsSm, fontWeight: T.fwSemibold, cursor: busy ? "wait" : "pointer", opacity: busy ? 0.7 : 1 }}>
            {saving ? "Saving…" : isCreate ? "Create project" : "Save changes"}
          </button>
          </div>
        </div>
      </form>
    </Modal>
    <ArchiveConfirmDialog isOpen={archiveOpen} objectLabel="project" busy={archiving} onCancel={() => { if (!mutation.current) setArchiveOpen(false); }} onConfirm={handleArchive} />
    </>
  );
}